import {z} from "zod/v4-mini"
import type {Vector2} from "@cm/math"
import {Vector3} from "@cm/math"
import {hashObject} from "@cm/utils"
import {
    curveObjectData,
    meshObjectData,
    Operators,
    Primitives,
    createMeshGeometryAttributeSetData,
    createGeometryAttributeRefData,
    createMeshGeometryAttributeData,
} from "@cm/template-nodes"
import {mattressParams, mattressAttrs} from "#mattress/geometry/mattress"
import type {
    CurveLikeNode,
    MeshLikeNode,
    GeometryGeneratorArgs,
    MeshGeometryAttributeSetLambda,
    GeomBuilderContext,
    AttributeRef,
    MeshGeometryAttributeSetData,
    IMeshGeometryQueries,
    ISceneManager,
    ReifiedMeshData,
    ReifiedCurveData,
} from "@cm/template-nodes"
import type {MattressTexUVLayout, MattressParams} from "#mattress/geometry/mattress"
import type {MattressMeshGeometryParametersLocalNodes} from "#mattress/nodes/component/mattress-component-types"

export const MATTRESS_LOCAL_NODE_FOR_BASE_MESH = "MattressLocalNodeForBaseMesh"
export const MATTRESS_LOCAL_NODE_FOR_SEGMENTED_MESH = "MattressLocalNodeForSegmentedMesh"

type ProjectedPointInfo = {
    manUVCoord: Vector2
    posCoord: Vector3
    partID: number
}

// adaptively subsample the cutting curve based on the mattress partID attribute part crossings
// returns subsampled cutting curve man UV coordinates
const subsampleCuttingCurveSegments = (curve: ReifiedCurveData, baseMesh: ReifiedMeshData, meshGeometryQueries: IMeshGeometryQueries) => {
    const points = curve.points
    const pointsCount = points.length / 3
    const segments: [Vector2, Vector2][] = []

    const projectCurvePointToMesh = (curvePointPosition: Vector3): ProjectedPointInfo => {
        const data = meshGeometryQueries.projectPointToMeshInPositionSpace(baseMesh, curvePointPosition)
        if (data === null || data === undefined) throw new Error("Failed to project curve point to base mesh in cutting curve preprocessing")
        const manUVCoord = data?.uvs[1]
        if (manUVCoord === undefined) throw new Error("Failed to project curve point to base mesh in cutting curve preprocessing")
        const posCoord = data?.position
        if (posCoord === undefined) throw new Error("Failed to project curve point to base mesh in cutting curve preprocessing")
        const partID = data?.materialID
        if (partID === undefined) throw new Error("Failed to project curve point to base mesh in cutting curve preprocessing")
        return {manUVCoord, posCoord, partID}
    }

    const subsampleSegment = (segmentStart: ProjectedPointInfo, segmentEnd: ProjectedPointInfo): [Vector2, Vector2][] => {
        if (segmentStart.partID === segmentEnd.partID) return [[segmentStart.manUVCoord, segmentEnd.manUVCoord]]

        const segment = segmentEnd.posCoord.sub(segmentStart.posCoord)
        const subsamplingLengthThreshold = 0.01
        if (segment.norm() <= subsamplingLengthThreshold) return [[segmentStart.manUVCoord, segmentEnd.manUVCoord]]

        const newPointPosition = segment.mul(0.5).add(segmentStart.posCoord)
        const newProjectedPointInfo = projectCurvePointToMesh(newPointPosition)

        return [...subsampleSegment(segmentStart, newProjectedPointInfo), ...subsampleSegment(newProjectedPointInfo, segmentEnd)]
    }

    let previousPoint: ProjectedPointInfo | null = null
    for (let idx = 0; idx < pointsCount; idx++) {
        const currentPoint = projectCurvePointToMesh(new Vector3(points[idx * 3], points[idx * 3 + 1], points[idx * 3 + 2]))
        if (previousPoint !== null) segments.push(...subsampleSegment(previousPoint, currentPoint))
        previousPoint = currentPoint
    }

    return [
        "float2",
        segments.map((seg) => [
            [seg[0].x, seg[0].y],
            [seg[1].x, seg[1].y],
        ]),
    ] as Primitives.AttributeData<"float2">
}

function parseMattressParamsFromArgs(args: GeometryGeneratorArgs): MattressParams {
    const parsedMattressParams = mattressParams.safeParse(args)
    if (!parsedMattressParams.success) throw new Error("Invalid mattress parameters encountered in mattress segmented mesh generation")
    return parsedMattressParams.data
}

///////////////////////////////////////////////////////////////////////////////////////////

export function getParametersForBaseMeshAttributesLocalNode(
    meshGeometryParametersLocalNodes: MattressMeshGeometryParametersLocalNodes,
): Omit<MeshGeometryAttributeSetLambda["parameters"], "name"> {
    const generatorFn = (
        ctx: {geomBuilderContext: GeomBuilderContext; sceneManager: ISceneManager},
        args: GeometryGeneratorArgs,
    ): MeshGeometryAttributeSetData => {
        const parsedMattressParams = parseMattressParamsFromArgs(args)
        const mattressAttrRefs = mattressAttrs(ctx.geomBuilderContext, parsedMattressParams, ["pos", "manUV", "partID", "texUV_Waterfall"])

        const createAttribute = (attrRef: AttributeRef) => createMeshGeometryAttributeData(createGeometryAttributeRefData(attrRef, attrRef, {}))

        const baseMeshAttrs = {
            position: mattressAttrRefs.pos,
            normal: mattressAttrRefs.pos.normals(1),
            materialID: mattressAttrRefs.partID,
            uvs: {
                texUV_Waterfall: mattressAttrRefs.texUV_Waterfall, // TODO remove? currently this only required for decals
                manUV: mattressAttrRefs.manUV,
            },
        }

        const triangulatedBaseMeshAttrs = Operators.triangulate(baseMeshAttrs.position, baseMeshAttrs)

        return createMeshGeometryAttributeSetData({
            position: createAttribute(triangulatedBaseMeshAttrs.position),
            normal: createAttribute(triangulatedBaseMeshAttrs.normal),
            materialID: createAttribute(triangulatedBaseMeshAttrs.materialID),
            uvs: createMeshGeometryAttributeSetData({
                uv0: createAttribute(triangulatedBaseMeshAttrs.uvs.texUV_Waterfall),
                uv1: createAttribute(triangulatedBaseMeshAttrs.uvs.manUV),
            }),
        })
    }

    const hash = hashObject({
        type: "mattressBaseMeshAttributesGenerator",
        meshGeometryParametersLocalNodes: Object.fromEntries(
            Object.entries(meshGeometryParametersLocalNodes).map(([key, value]) => [key, value?.instanceId ?? null]),
        ),
    })

    return {
        args: meshGeometryParametersLocalNodes,
        generator: {fn: generatorFn, hash},
    }
}

///////////////////////////////////////////////////////////////////////////////////////////

export const DUMMY_UVS_IDX_OFFSET = 1

export type MattressMeshSegmentationInfo = {
    baseData: {texUVLayout: MattressTexUVLayout; materialID: number}
    cutsData: {conditions: {curveID: string; side: "left" | "right"}[]; materialID: number; texUVLayout: MattressTexUVLayout}[]
    curvesData: {[curveID: string]: CurveLikeNode}
    changeDetectionHash: string
}

export function getParametersForSegmentedMeshAttributesLocalNode(
    segmentationInfo: MattressMeshSegmentationInfo,
    baseMeshLocalNode: MeshLikeNode,
    meshGeometryParametersLocalNodes: MattressMeshGeometryParametersLocalNodes,
): Omit<MeshGeometryAttributeSetLambda["parameters"], "name"> {
    const {curvesData, cutsData, baseData} = segmentationInfo

    const anyCurvesDefined = Object.keys(curvesData).length > 0

    const getBaseMeshAttrs = (geometryBuilderContext: GeomBuilderContext, mattressParams: MattressParams) => {
        const requiredTexUVLayouts = new Set<MattressTexUVLayout>()
        requiredTexUVLayouts.add(baseData.texUVLayout)
        cutsData.forEach((cutData) => {
            requiredTexUVLayouts.add(cutData.texUVLayout)
        })
        const mattressAttrRefs = mattressAttrs(geometryBuilderContext, mattressParams, ["pos", "manUV", ...requiredTexUVLayouts.values()])

        const texUVs: {[K: string]: AttributeRef} = {}
        texUVs[baseData.texUVLayout] = mattressAttrRefs[baseData.texUVLayout]
        cutsData.forEach((cutData) => {
            texUVs[cutData.texUVLayout] = mattressAttrRefs[cutData.texUVLayout]
        })

        return {
            position: mattressAttrRefs.pos,
            normal: mattressAttrRefs.pos.normals(1),
            cuttingUV: mattressAttrRefs.manUV,
            texUVs,
        }
    }

    const triangulateStandardMeshAttrs = (attrs: {
        position: AttributeRef
        normal: AttributeRef
        materialID: AttributeRef
        uvs: {[K: string]: AttributeRef}
    }) => {
        const auxSet: {[K: string]: AttributeRef} = {
            position: attrs.position,
            normal: attrs.normal,
            materialID: attrs.materialID,
            ...attrs.uvs,
        }

        const triangulatedAuxSet = Operators.triangulate(auxSet.position, auxSet)
        return {
            position: triangulatedAuxSet.position,
            normal: triangulatedAuxSet.normal,
            materialID: triangulatedAuxSet.materialID,
            uvs: Object.fromEntries(Object.keys(attrs.uvs).map((uvKey) => [uvKey, triangulatedAuxSet[uvKey]])),
        }
    }

    const createAttributeFromRef = (attrRef: AttributeRef) => createMeshGeometryAttributeData(createGeometryAttributeRefData(attrRef, attrRef, {}))

    const {generatorFn, args} = (() => {
        if (!anyCurvesDefined) {
            const generatorFn = (
                ctx: {geomBuilderContext: GeomBuilderContext; sceneManager: ISceneManager},
                args: GeometryGeneratorArgs,
            ): MeshGeometryAttributeSetData => {
                const parsedMattressParams = parseMattressParamsFromArgs(args)
                const baseMeshAttrs = getBaseMeshAttrs(ctx.geomBuilderContext, parsedMattressParams)

                const outputMeshAttrs = {
                    position: baseMeshAttrs.position,
                    normal: baseMeshAttrs.normal,
                    materialID: baseMeshAttrs.position.constInt(baseData.materialID),
                    uvs: {
                        uv0: baseMeshAttrs.texUVs[baseData.texUVLayout],
                    },
                }
                const triangulatedOutputMeshAttrs = triangulateStandardMeshAttrs(outputMeshAttrs)

                return createMeshGeometryAttributeSetData({
                    position: createAttributeFromRef(triangulatedOutputMeshAttrs.position),
                    normal: createAttributeFromRef(triangulatedOutputMeshAttrs.normal),
                    materialID: createAttributeFromRef(triangulatedOutputMeshAttrs.materialID),
                    uvs: createMeshGeometryAttributeSetData({
                        uv0: createAttributeFromRef(triangulatedOutputMeshAttrs.uvs.uv0),
                    }),
                })
            }
            return {generatorFn, args: meshGeometryParametersLocalNodes}
        } else {
            const generatorFn = (
                ctx: {geomBuilderContext: GeomBuilderContext; sceneManager: ISceneManager},
                args: GeometryGeneratorArgs,
            ): MeshGeometryAttributeSetData => {
                const curveIDs = Object.keys(curvesData)

                const meshGeometryQueries = ctx.sceneManager.getMeshGeometryQueries()
                if (!meshGeometryQueries) throw new Error("Mesh geometry queries are required for mattress cutting curve preprocessing")

                const parsedMattressParams = parseMattressParamsFromArgs(args)
                const baseMeshAttrs = getBaseMeshAttrs(ctx.geomBuilderContext, parsedMattressParams)

                const parsedBaseMeshObjectData = z.object({baseMesh: meshObjectData}).safeParse(args)
                if (!parsedBaseMeshObjectData.success) throw new Error("Invalid base mesh data provided to mattress segmented mesh generator")
                const reifiedBaseMeshData = parsedBaseMeshObjectData.data.baseMesh.completeMeshData.reified

                const parsedCurvesObjectData = z
                    .object(
                        (() => {
                            const curveDataShape: {[curveID: string]: typeof curveObjectData} = {}
                            curveIDs.forEach((curveID) => {
                                curveDataShape[curveID] = curveObjectData
                            })
                            return curveDataShape
                        })(),
                    )
                    .safeParse(args)
                if (!parsedCurvesObjectData.success) throw new Error("Invalid curves data provided to mattress segmented mesh generator")
                const reifiedCurvesData: {[curveID: string]: ReifiedCurveData} = {}
                const parsedCurvesData = parsedCurvesObjectData.data as {[curveID: string]: z.infer<typeof curveObjectData>}
                curveIDs.forEach((curveID) => {
                    const reifiedCurveData = parsedCurvesData[curveID].completeCurveData.reified
                    if (reifiedCurveData === null || reifiedCurveData === undefined)
                        throw new Error(`Missing reified data for cutting curve ID ${curveID} in mattress segmented mesh generator`)
                    reifiedCurvesData[curveID] = reifiedCurveData
                })

                const cuttingCurves: {[curveID: string]: {cuttingUV: AttributeRef; curveID: AttributeRef}} = {}
                curveIDs.forEach((curveID) => {
                    const {cuttingUV} = Primitives.curve(ctx.geomBuilderContext, {
                        cuttingUV: subsampleCuttingCurveSegments(reifiedCurvesData[curveID], reifiedBaseMeshData, meshGeometryQueries),
                    })

                    cuttingCurves[curveID] = {
                        cuttingUV,
                        curveID: cuttingUV.constInt(parseInt(curveID)),
                    }
                })
                const mergedCuttingCurves = Operators.merge(
                    ...curveIDs.map((curveID) => ({
                        cuttingUV: cuttingCurves[curveID].cuttingUV,
                        curveID: cuttingCurves[curveID].curveID,
                    })),
                )

                const [cutMeshAttrs, windingAttrs] = Operators.cut(
                    baseMeshAttrs.cuttingUV,
                    baseMeshAttrs,
                    mergedCuttingCurves.cuttingUV,
                    mergedCuttingCurves.curveID,
                    curveIDs.map((id) => parseInt(id)),
                )

                // attribute tracking sidedness of cut mesh vertices for each cutting curve
                // 1 left side, 0 right side
                const getCurveSideCondition = (curveID: string, side: "left" | "right"): AttributeRef => {
                    if (side === "left") {
                        const windingAttr = windingAttrs[parseInt(curveID)]
                        const windingThreshold = Operators.getCutThreshold(windingAttr)
                        return windingAttr.lt(windingThreshold)
                    } else {
                        const leftSideCondition = getCurveSideCondition(curveID, "left")
                        return leftSideCondition.eq(0)
                    }
                }

                const curvesSideDummyUV: {[curveID: string]: AttributeRef} = {}
                curveIDs.forEach((curveID) => {
                    const leftSideCondition = getCurveSideCondition(curveID, "left")
                    curvesSideDummyUV[curveID] = Operators.pack(leftSideCondition, leftSideCondition.constFloat(0))
                })

                const cutMeshBaseMaterialID = cutMeshAttrs.position.constInt(baseData.materialID)
                let materialID = cutMeshBaseMaterialID
                let texUV = cutMeshAttrs.texUVs[baseData.texUVLayout]

                // compute materialID based on cut conditions
                cutsData.forEach((cutData) => {
                    if (cutData.conditions.length === 0) return

                    let cumulativeCutCondition: AttributeRef | null = null
                    for (let i = 0; i < cutData.conditions.length; i++) {
                        const condition = cutData.conditions[i]
                        const curveSideCondition = getCurveSideCondition(condition.curveID, condition.side)
                        cumulativeCutCondition = cumulativeCutCondition === null ? curveSideCondition : cumulativeCutCondition.and(curveSideCondition)
                    }
                    if (!cumulativeCutCondition) throw new Error("Cummulative condition is null after processing cut conditions")
                    materialID = cumulativeCutCondition.select(cutData.materialID, materialID)
                    texUV = cumulativeCutCondition.select(cutMeshAttrs.texUVs[cutData.texUVLayout], texUV)
                })

                materialID = materialID.castInt()

                const outputMeshUVAttrs = {
                    uv0: texUV,
                    ...(() => {
                        const extraUVs: {[key: string]: AttributeRef} = {}
                        Object.keys(curvesSideDummyUV).forEach((curveID, idx) => {
                            extraUVs[`uv${idx + DUMMY_UVS_IDX_OFFSET}`] = curvesSideDummyUV[curveID]
                        })
                        return extraUVs
                    })(),
                }

                const outputMeshAttrs = {
                    position: cutMeshAttrs.position,
                    normal: cutMeshAttrs.normal,
                    materialID: materialID,
                    uvs: outputMeshUVAttrs,
                }

                const triangulatedOutputMeshAttrs = triangulateStandardMeshAttrs(outputMeshAttrs)

                return createMeshGeometryAttributeSetData({
                    position: createAttributeFromRef(triangulatedOutputMeshAttrs.position),
                    normal: createAttributeFromRef(triangulatedOutputMeshAttrs.normal),
                    materialID: createAttributeFromRef(triangulatedOutputMeshAttrs.materialID),
                    uvs: createMeshGeometryAttributeSetData(
                        Object.fromEntries(
                            Object.entries(triangulatedOutputMeshAttrs.uvs).map(([uvName, uvAttrRef]) => [uvName, createAttributeFromRef(uvAttrRef)]),
                        ),
                    ),
                })
            }
            return {generatorFn, args: {...meshGeometryParametersLocalNodes, ...curvesData, baseMesh: baseMeshLocalNode}}
        }
    })()

    return {
        args,
        generator: {fn: generatorFn, hash: segmentationInfo.changeDetectionHash},
    }
}
